import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Button,
  Box,
  Typography,
  Card,
  Checkbox,
  Drawer,
  Chip,
} from '@mui/material';
import PropTypes from 'prop-types';
import Iconify from '../common/iconify.component';
import SnackbarInfo from '../common/snackbar-info.component';
import DistributorConnectedSources from '../form-elements/distributor-connected-sources.component';
import {
  getListData,
  pullDataFromS3,
  pushDataToCMS,
} from '../../services/content-sources.service';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/content-sources.config';
import {
  ACTIONS,
  CONNECTED_SOURCE_FOLDER_SYNC_STATUS,
} from '../../config/const.config';
import PreviewContentSource from './preview-content-source.component';
import ListContentSourcesData from '../table-elements/list-content-sources-data.component';
import ConfirmPopup from '../common/confirm-popup.component';
import FilterContentSources from './filter-content-sources.component';
import { FiltersContext } from '../../context/filter.context';
import {
  getLocalFilterValue,
  setLocalFilterValue,
} from '../../utils/local-cache.util';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ListContentSources = ({ showFilters, showFiltersCallback }) => {
  const FILTERS_KEY = 'content-sources-filters';
  const columns = LISTING_COLUMNS;
  const actions = LISTING_ACTIONS;

  const { getSignal, isAbortError } = useAbortableRequest();

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: {
      search: { name: 'Search', value: '' },
      status: { name: 'Status', value: [] },
      type: { name: 'Type', value: null },
    },
    metrics: {},
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [selectedConnectedSource, setSelectedConnectedSource] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [showConfirmationDailog, setShowConfirmationDailog] = useState(false);

  const { openFilter, handleToggleFilters } = useContext(FiltersContext);

  const filteredRowsList = useMemo(
    () =>
      options.rows.filter(
        (item) =>
          item.status === CONNECTED_SOURCE_FOLDER_SYNC_STATUS.NEW.value ||
          item.status === CONNECTED_SOURCE_FOLDER_SYNC_STATUS.ERROR.value ||
          item.status ===
            CONNECTED_SOURCE_FOLDER_SYNC_STATUS.MISSING_CSV.value ||
          item.status ===
            CONNECTED_SOURCE_FOLDER_SYNC_STATUS.MISSING_ASSETS.value
      ),
    [options.rows]
  );

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleApplyFilters = (selectedFilters) => {
    setLocalFilterValue(FILTERS_KEY, selectedFilters);

    setOptions({
      ...options,
      appliedFilters: {
        ...selectedFilters,
      },
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleClearFilters = () => {
    setLocalFilterValue(FILTERS_KEY, {});
    setOptions({
      ...options,
      appliedFilters: {
        search: { name: 'Search', value: '' },
        status: { name: 'Status', value: [] },
        type: { name: 'Type', value: null },
      },
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleDeleteFilter = (field) => {
    if (field === 'clear-all') {
      handleClearFilters();
      return;
    }
    const updatedFilters = { ...options.appliedFilters };
    updatedFilters[field] = {
      ...updatedFilters[field],
      value: Array.isArray(updatedFilters[field]?.value) ? [] : null || '',
    };
    setLocalFilterValue(FILTERS_KEY, updatedFilters);

    setOptions({
      ...options,
      appliedFilters: updatedFilters,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const pushToCMSApiCall = (payload) => {
    pushDataToCMS(payload)
      .then((res) => {
        if (res.data) {
          setSnackbarInfo({
            ...snackbarInfo,
            show: true,
            type: 'success',
            message: 'Data has been moved to queue successfully.',
          });
          handleActionCancel();
          handleRefreshData();
          setShowConfirmationDailog(false);
        }
        setProcessing(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
        setProcessing(false);
      });
  };

  const handlePullS3Data = () => {
    setProcessing(true);

    pullDataFromS3(selectedConnectedSource.id)
      .then((res) => {
        if (res.data) {
          handleRefreshData();
        }
        setProcessing(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setProcessing(false);
      });
  };

  const handlePushToCMS = (id = null) => {
    setSnackbarInfo({ ...snackbarInfo, show: false });

    const payload = {
      sourceFolderIds: id ? [id] : selectedRows,
    };
    if (!id) {
      payload.processAllData = selectAllPages;
    }
    pushToCMSApiCall(payload);
  };

  useEffect(() => {
    const signal = getSignal();

    if (selectedConnectedSource?.id) {
      const localFilters = getLocalFilterValue(FILTERS_KEY);

      setOptions({ ...options, loading: true });
      setSelectAllPages(false);
      setSelectedRows([]);
      const params = [];
      params.push(`page=${options.page + 1}`);
      params.push(`perPage=${options.rowsPerPage}`);
      if (options.sortBy && options.sortOrder) {
        params.push(`sortBy=${options.sortBy}`);
        params.push(`sortOrder=${options.sortOrder}`);
      }
      const appliedFilters =
        Object.keys(localFilters).length > 0
          ? localFilters
          : options.appliedFilters;
      const filters = Object.keys(appliedFilters).reduce((acc, key) => {
        acc[key] = appliedFilters[key].value;
        return acc;
      }, {});
      if (filters.search) {
        params.push(`q=${encodeURIComponent(filters.search)}`);
      }
      if (filters.status?.length > 0) {
        params.push(`status=${filters.status.join(',')}`);
      }
      if (filters.type?.id) {
        params.push(`type=${filters.type.id}`);
      }

      const uri = '';
      const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
      getListData(selectedConnectedSource.id, uri + paramsQuery, signal)
        .then((res) => {
          if (!signal.aborted) {
            setOptions({
              ...options,
              loading: false,
              totalRows: res?.data?.totalRows || 0,
              rows: res?.data?.rows || [],
              metrics: res?.data?.metrics || {},
              appliedFilters: appliedFilters || {},
              error: false,
            });
          }
        })
        .catch((error) => {
          if (isAbortError(error)) {
            return;
          }
          setOptions({
            ...options,
            loading: false,
            page: 0,
            totalRows: 0,
            rows: [],
            metrics: {},
            error: true,
          });
        });
    }
  }, [options.reloadCounter, selectedConnectedSource?.id]);

  useEffect(() => {
    if (selectedConnectedSource?.id) {
      showFiltersCallback(true);
    } else {
      showFiltersCallback(false);
    }
  }, [selectedConnectedSource?.id]);

  return (
    <Box
      sx={{
        transition: 'margin 0.3s ease, width 0.3s ease',
        width: openFilter ? 'calc(100% - 300px)' : '100%',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 0, py: 2 }}
      >
        <Box width="60%">
          <Box display="flex" alignItems="center" gap={1}>
            <DistributorConnectedSources
              id="distributor-connected-sources"
              name="distributor-connected-sources"
              label="Distributor Connected Sources"
              defaultValue={selectedConnectedSource}
              onChange={(v) => setSelectedConnectedSource(v)}
              sx={{ width: '300px' }}
              disabled={processing}
            />

            {selectedConnectedSource && (
              <>
                <Box display="flex" alignItems="center">
                  <Checkbox
                    checked={selectAllPages}
                    onChange={() => {
                      setSelectAllPages(!selectAllPages);
                      setSelectedRows([]);
                    }}
                    disabled={
                      options.loading ||
                      !options.rows.length ||
                      !filteredRowsList.length
                    }
                  />
                  <Typography variant="body2" color="text.secondary">
                    Select All
                  </Typography>
                </Box>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={() => setShowConfirmationDailog(true)}
                  disabled={!(selectAllPages || selectedRows.length > 0)}
                >
                  Push To CMS
                </Button>
              </>
            )}
          </Box>

          {!selectedConnectedSource && (
            <Typography variant="body2" sx={{ mt: 2, color: 'grey' }}>
              Please Select Distributor Connected Source
            </Typography>
          )}
        </Box>

        {selectedConnectedSource && (
          <Box>
            <Button
              type="button"
              color="primary"
              variant="contained"
              sx={{ mr: 1 }}
              onClick={() => handlePullS3Data()}
              disabled={
                options.loading || processing || !selectedConnectedSource
              }
            >
              Pull from S3
            </Button>
            <Button
              color="inherit"
              variant="contained"
              startIcon={<Iconify icon="ic:twotone-refresh" />}
              onClick={handleRefreshData}
              disabled={
                options.loading || processing || !selectedConnectedSource
              }
            >
              Refresh
            </Button>
          </Box>
        )}
      </Stack>

      {selectedConnectedSource && (
        <Stack direction="row" sx={{ px: 0, py: 1 }}>
          <Box display="flex">
            <Box width={280}>
              <Typography variant="body2" mb={1} fontSize={12}>
                Discovered Titles:{' '}
                <strong>
                  {options.metrics?.titles?.discoveredTitles || 0}
                </strong>
              </Typography>
              <Typography variant="body2" mb={1} fontSize={12}>
                Titles Missing Assets:{' '}
                <strong>
                  {options.metrics?.titles?.missingAssetsTitles || 0}
                </strong>
              </Typography>
              <Typography variant="body2" mb={1} fontSize={12}>
                New Titles (Not Queued):{' '}
                <strong>{options.metrics?.titles?.newTitles || 0}</strong>
              </Typography>
              <Typography variant="body2" mb={1} fontSize={12}>
                Queued Titles:{' '}
                <strong>{options.metrics?.titles?.queuedTitles || 0}</strong>
              </Typography>
              <Typography variant="body2" mb={1} fontSize={12}>
                Encoded Titles:{' '}
                <strong>{options.metrics?.titles?.encodedTitles || 0}</strong>
              </Typography>
              <Typography variant="body2" mb={1} fontSize={12}>
                Titles Missing CSV:{' '}
                <strong>
                  {options.metrics?.titles?.missingCSVTitles || 0}
                </strong>
              </Typography>
              <Typography variant="body2" mb={1} fontSize={12}>
                Failed Titles:{' '}
                <strong>{options.metrics?.titles?.errorTitles || 0}</strong>{' '}
              </Typography>
            </Box>

            <Box width={280}>
              <Typography variant="body2" mb={1} fontSize={12}>
                Discovered Videos:{' '}
                <strong>
                  {options.metrics?.videos?.discoveredVideos || 0}
                </strong>
              </Typography>
              <Typography variant="body2" mb={1} fontSize={12}>
                Encoding Errors:{' '}
                <strong>{options.metrics?.videos?.encodingErrors || 0}</strong>
              </Typography>
              <Typography variant="body2" mb={1} fontSize={12}>
                New Videos (Not Queued):{' '}
                <strong>{options.metrics?.videos?.newVideos || 0}</strong>
              </Typography>
              <Typography variant="body2" mb={1} fontSize={12}>
                Queued Videos:{' '}
                <strong>{options.metrics?.videos?.queuedVideos || 0}</strong>
              </Typography>
              <Typography variant="body2" mb={1} fontSize={12}>
                Encoded Videos:{' '}
                <strong>{options.metrics?.videos?.encodedVideos || 0}</strong>
              </Typography>
            </Box>

            <Box width={280}>
              <Typography variant="body2" mb={1} fontSize={12}>
                Active Encoding:{' '}
                <strong>{options.metrics?.activeEncoding || 0}</strong>
              </Typography>
            </Box>
          </Box>
        </Stack>
      )}

      {selectedConnectedSource &&
        options.appliedFilters &&
        Object.entries(options.appliedFilters).some(
          ([key, filter]) =>
            ((Array.isArray(filter.value) && filter.value.length > 0) ||
              (!Array.isArray(filter.value) &&
                filter.value !== '' &&
                filter.value !== null)) &&
            key !== 'playlistLevel'
        ) && (
          <Box mb={2}>
            <Typography variant="body1" fontWeight="bold" display="inline">
              Filters Applied:{' '}
              <Chip
                key="all"
                label="Clear all"
                onDelete={() => handleDeleteFilter('clear-all')}
                sx={{ ml: 1, color: 'red' }}
              />
            </Typography>
            {Object.entries(options.appliedFilters)
              .filter(
                ([key, filter]) =>
                  ((Array.isArray(filter.value) && filter.value.length > 0) ||
                    (!Array.isArray(filter.value) &&
                      filter.value !== '' &&
                      filter.value !== null)) &&
                  key !== 'playlistLevel'
              )
              .map(([field, filter]) => (
                <Chip
                  key={field}
                  label={filter.name || 'Active'}
                  onDelete={() => handleDeleteFilter(field)}
                  sx={{ ml: 1 }}
                />
              ))}
          </Box>
        )}

      {selectedConnectedSource && (
        <Card>
          <ListContentSourcesData
            columns={columns}
            rows={options.rows}
            page={options.page}
            rowsPerPage={options.rowsPerPage}
            totalRows={options.totalRows}
            loading={options.loading}
            actions={actions}
            error={options.error}
            sortBy={options.sortBy}
            sortOrder={options.sortOrder}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSortChange={handleSortingChange}
            onAction={handleActionSelect}
            showCheckbox
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            selectAllPages={selectAllPages}
            setSelectAllPages={setSelectAllPages}
          />
        </Card>
      )}

      {showFilters && (
        <Drawer
          anchor="right"
          open={openFilter}
          variant="persistent"
          PaperProps={{
            sx: { width: 300, border: 'none', overflow: 'hidden' },
          }}
        >
          <FilterContentSources
            values={options.appliedFilters}
            onCancel={handleToggleFilters}
            onSuccess={handleApplyFilters}
            onClear={handleClearFilters}
          />
        </Drawer>
      )}

      {doAction.action === ACTIONS.PREVIEW.value && (
        <PreviewContentSource
          title={`Preview - ${doAction.data.folderName}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
        />
      )}

      {(doAction.action === ACTIONS.PUSH_TO_CMS.value ||
        doAction.action === ACTIONS.RETRY.value) && (
        <ConfirmPopup
          title={`${
            doAction.action === ACTIONS.RETRY.value ? 'Retry' : 'Push to CMS'
          } Confirmation`}
          message={`Do you want to add ${doAction.data.folderName} title into queue? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={() => handlePushToCMS(doAction.data.id)}
        />
      )}

      {showConfirmationDailog && (
        <ConfirmPopup
          title="Push to CMS Confirmation"
          message="Do you want to add selected titles into queue? You can not undo this action!"
          onCancel={() => setShowConfirmationDailog(false)}
          onSuccess={() => handlePushToCMS()}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

ListContentSources.propTypes = {
  showFilters: PropTypes.bool.isRequired,
  showFiltersCallback: PropTypes.func.isRequired,
};

export default ListContentSources;
